import type { FC } from "react";

import type { RepositoryChatConversation } from "../../dashboard-client";

import { EmptyState } from "../common/EmptyState";
import { LoadingSkeleton } from "../common/LoadingState";
import {
  formatIsoRelativeTime,
  getConversationSubtitle,
  getConversationTitle,
} from "./repositoryChatModels";

interface RepositoryChatConversationListProps {
  conversations: RepositoryChatConversation[];
  loadingConversations: boolean;
  onSelectConversation: (conversationId: string) => void;
  selectedConversationId: string | null;
}

export const RepositoryChatConversationList: FC<
  RepositoryChatConversationListProps
> = ({
  conversations,
  loadingConversations,
  onSelectConversation,
  selectedConversationId,
}) => {
  const content = renderConversationListContent(
    conversations,
    loadingConversations,
    onSelectConversation,
    selectedConversationId,
  );

  return (
    <section className="space-y-3">
      <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
        Recent chats
      </p>
      <div className="space-y-2">{content}</div>
    </section>
  );
};

const ConversationCard: FC<{
  conversation: RepositoryChatConversation;
  isActive: boolean;
  onSelectConversation: (conversationId: string) => void;
}> = ({ conversation, isActive, onSelectConversation }) => (
  <button
    className={`w-full border px-3 py-3 text-left transition-colors ${
      isActive
        ? "border-emerald-500 bg-emerald-50 text-slate-900 dark:bg-slate-950 dark:text-white"
        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-800"
    }`}
    onClick={() => onSelectConversation(conversation.conversationId)}
    type="button"
  >
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-1">
        <div className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-500 dark:text-emerald-400">
          {formatIsoRelativeTime(conversation.updatedAt)}
        </div>
        <div className="text-sm font-semibold">
          {getConversationTitle(conversation)}
        </div>
        <div
          className={`text-xs ${
            isActive
              ? "text-slate-500 dark:text-slate-300"
              : "text-slate-500 dark:text-slate-400"
          }`}
        >
          {getConversationSubtitle(conversation)}
        </div>
      </div>
      <span
        className={`font-mono text-[10px] font-bold uppercase tracking-[0.18em] ${
          isActive
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-slate-400 dark:text-slate-500"
        }`}
      >
        {conversation.messages?.length ?? 0} msgs
      </span>
    </div>
  </button>
);

function renderConversationListContent(
  conversations: RepositoryChatConversation[],
  loadingConversations: boolean,
  onSelectConversation: (conversationId: string) => void,
  selectedConversationId: string | null,
) {
  if (loadingConversations) {
    return <LoadingSkeleton rows={4} rowHeight="h-20" />;
  }

  if (conversations.length === 0) {
    return (
      <EmptyState
        size="sm"
        title="No conversations yet"
        description="Start a repository chat from the scope builder above."
      />
    );
  }

  return conversations.map((conversation) => (
    <ConversationCard
      key={conversation.conversationId}
      conversation={conversation}
      isActive={conversation.conversationId === selectedConversationId}
      onSelectConversation={onSelectConversation}
    />
  ));
}
